'use client';

import { useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';

import type {
  ComplianceJurisdiction,
  FormFieldDescriptor,
} from '../types/vertical';
import { formFieldsToZodSchema } from '../types/vertical';
import { ConsentCheckbox, getConsentText } from './ConsentCheckbox';
import { DynamicFormStep } from './DynamicFormStep';
import { FormProgressBar } from './FormProgressBar';
import { PhoneInput } from './PhoneInput';

/** UTM, click ID, and experiment tracking fields extracted from the URL and cookies. */
interface TrackingParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  fbp?: string;
  fbc?: string;
  gclid?: string;
  experimentVariant?: string;
}

/**
 * Serializable subset of VerticalConfig for the client.
 * Avoids passing Zod schemas (class instances) across the server/client boundary.
 */
interface SerializableVerticalConfig {
  name: string;
  compliance: { jurisdiction: ComplianceJurisdiction };
}

interface LeadCaptureFormProps {
  verticalConfig: SerializableVerticalConfig;
  citySlug: string;
  verticalSlug: string;
  /** Pre-computed form field descriptors (plain objects, serializable). */
  customFormFields: FormFieldDescriptor[];
  submitAction: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
  /** Saves core fields after Step 1 without dispatching the pipeline. */
  partialSaveAction?: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
  onSuccess?: (leadId?: string) => void;
  /** UTM and experiment tracking fields to persist on the lead record. */
  tracking?: TrackingParams;
  /** Phone input placeholder (e.g. "+41 79 123 45 67"). */
  phonePlaceholder?: string;
  /**
   * Whether phone is required at capture. Defaults to true. Verticals
   * that run email-only capture (e.g. B2B) can pass false to relax the
   * client validation; server still enforces via vertical config.
   */
  requirePhone?: boolean;
  /**
   * Optional pre-filled values for the dynamic (custom) fields on Step 2.
   * Shallow-merged into `defaultValues.customFields` so the user can still
   * change any field before submitting. Used by lead-magnet pages (e.g.
   * a cost calculator) that have already captured part of the vertical's
   * custom-field shape.
   */
  initialCustomFields?: Record<string, unknown>;
}

/**
 * Multi-step lead capture form.
 *
 * Step 1: Core fields (firstName, lastName, email, phone) + consent checkbox.
 * Step 2+: Dynamic custom fields from the vertical config, rendered via DynamicFormStep.
 *
 * On final submit, calls the provided server action.
 */
export function LeadCaptureForm({
  verticalConfig,
  citySlug,
  verticalSlug,
  customFormFields,
  submitAction,
  partialSaveAction,
  onSuccess,
  tracking,
  phonePlaceholder,
  requirePhone = true,
  initialCustomFields,
}: LeadCaptureFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitState, setSubmitState] = useState<{
    success?: boolean;
    error?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partialLeadId, setPartialLeadId] = useState<string | null>(null);
  const partialSavePromiseRef = useRef<Promise<{
    success: boolean;
    leadId?: string;
  }> | null>(null);

  const totalSteps = customFormFields.length > 0 ? 2 : 1;

  // Build validation schema client-side from pre-computed field descriptors.
  // `requirePhone` flows from the vertical config so verticals can relax
  // the phone requirement without touching the shared schema builder.
  const formSchema = useMemo(
    () => formFieldsToZodSchema(customFormFields, { requirePhone }),
    [customFormFields, requirePhone],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      consentGiven: false,
      consentText: '',
      customFields: {
        ...(initialCustomFields ?? {}),
      } as Record<string, unknown>,
    },
    mode: 'onTouched',
  });

  const handleNext = async () => {
    // Validate step 1 fields before proceeding
    const step1Fields = [
      'phone',
      'firstName',
      'lastName',
      'email',
      'consentGiven',
    ] as const;

    const isValid = await form.trigger(step1Fields as unknown as string[]);

    if (!isValid) {
      return;
    }

    // Fire-and-forget partial save. Never blocks step 2 advancement.
    // If save fails or hangs, the final submit creates a full lead as fallback.
    if (partialSaveAction && !partialLeadId) {
      const consentText = getConsentText(
        verticalConfig.compliance.jurisdiction,
        verticalConfig.name,
      );

      const values = form.getValues();

      const savePromise = partialSaveAction({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email || '',
        phone: values.phone,
        consentGiven: values.consentGiven,
        consentText,
        verticalSlug,
        citySlug,
        ...tracking,
      })
        .then((result) => {
          if (result.success && result.leadId) {
            setPartialLeadId(result.leadId);
            // Make the leadId available to sibling components (e.g. intent signal tracking)
            try {
              sessionStorage.setItem(
                `leadgen_partial_lead_id:${verticalSlug}`,
                result.leadId,
              );
            } catch {
              // sessionStorage unavailable (private mode, etc.) - non-critical
            }
          }

          return result;
        })
        .catch(() => {
          return { success: false as const };
        });

      partialSavePromiseRef.current = savePromise;
    }

    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const onSubmit = async (data: FieldValues) => {
    setIsSubmitting(true);
    setSubmitState({});

    try {
      // Inject consent text based on jurisdiction
      const consentText = getConsentText(
        verticalConfig.compliance.jurisdiction,
        verticalConfig.name,
      );

      // Await the partial save if it hasn't resolved yet, so we have the
      // leadId before submitting. Without this, a fast Step 2 submit races
      // against the in-flight partial save and takes the "create new lead"
      // path, which fails with a duplicate phone error.
      let resolvedLeadId = partialLeadId;

      if (!resolvedLeadId && partialSavePromiseRef.current) {
        try {
          const partialResult = await partialSavePromiseRef.current;

          if (partialResult.success && partialResult.leadId) {
            resolvedLeadId = partialResult.leadId;
            setPartialLeadId(partialResult.leadId);
          }
        } catch {
          // Proceed without leadId; submitAction will create a new lead
        }
      }

      const result = await submitAction({
        ...(data as Record<string, unknown>),
        consentText,
        verticalSlug,
        citySlug,
        // Pass leadId from partial save so Step 2 updates existing lead
        ...(resolvedLeadId ? { leadId: resolvedLeadId } : {}),
        ...tracking,
      });

      if (result.success) {
        setSubmitState({ success: true });
        onSuccess?.(result.leadId);
      } else {
        setSubmitState({
          error: result.error || 'Something went wrong. Please try again.',
        });
      }
    } catch {
      setSubmitState({
        error: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitState.success) {
    return (
      <Alert variant="success">
        <AlertTitle>Application Submitted</AlertTitle>
        <AlertDescription>
          Thank you for your interest in {verticalConfig.name}. We will be in
          touch shortly.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormProgressBar currentStep={currentStep} totalSteps={totalSteps} />

        {submitState.error ? (
          <Alert variant="destructive">
            <AlertTitle>Submission Error</AlertTitle>
            <AlertDescription>{submitState.error}</AlertDescription>
          </Alert>
        ) : null}

        {/* Step 1: Core fields + consent */}
        {currentStep === 1 ? (
          <div className="flex flex-col gap-4">
            <FormField
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Phone Number
                    {requirePhone ? (
                      <span
                        aria-hidden="true"
                        className="text-destructive ml-1"
                      >
                        *
                      </span>
                    ) : null}
                  </FormLabel>
                  <FormControl>
                    <PhoneInput
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      required={requirePhone}
                      placeholder={phonePlaceholder}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="First name"
                        data-test="field-firstName"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Last name"
                        data-test="field-lastName"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email (optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      data-test="field-email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="consentGiven"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ConsentCheckbox
                      jurisdiction={verticalConfig.compliance.jurisdiction}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      verticalName={verticalConfig.name}
                      error={
                        form.formState.errors.consentGiven?.message as
                          | string
                          | undefined
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        ) : null}

        {/* Step 2: Dynamic vertical-specific fields */}
        {currentStep === 2 && customFormFields.length > 0 ? (
          <DynamicFormStep
            fields={customFormFields}
            fieldPrefix="customFields."
          />
        ) : null}

        {/* Navigation buttons */}
        <div className="flex gap-3">
          {currentStep > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              data-test="form-back-button"
            >
              Back
            </Button>
          ) : null}

          {currentStep < totalSteps ? (
            <Button
              type="button"
              onClick={handleNext}
              className="ml-auto"
              data-test="form-next-button"
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSubmitting}
              className="ml-auto"
              data-test="form-submit-button"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
